/* Custom Playmat Designer — pmt- namespace.
 * ---------------------------------------------------------------------------
 * Third designer tool. The proven Metro plumbing (option proxying, artwork
 * gate, buy-button adoption, variant watching) is ported from the deck box
 * tool; everything visual is new and deliberately lighter: the preview is
 * CSS 2.5D driven by two canvases — NO three.js on this template at all.
 *
 * Metro traps this file already knows about (hard-won on the other tools):
 * - product-info.js ignores option changes whose event target is outside
 *   <product-info>, so the tool's chips CLICK the real (hidden) inputs.
 * - Metro re-renders ProductBuyButtons' innerHTML on every variant change,
 *   destroying injected inputs — ensureFormInputs re-runs after each change.
 * - form.id on a product form returns the <input name="id"> element, not the
 *   id attribute. Always getAttribute('id').
 * - shopify:section:load fires on EVERY editor tweak — boot() guards re-init.
 */
(function () {
  'use strict';

  var STACK_BREAKPOINT = 899;   // must match the media query in the CSS

  function boot() {
    var roots = document.querySelectorAll('[data-playmat-designer]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__pmtInit) continue;
      roots[i].__pmtInit = true;
      try {
        new PlaymatDesigner(roots[i]);
      } catch (err) {
        roots[i].__pmtInit = false;
        console.error('[playmat-designer] init failed', err);
      }
    }
  }

  // ---------------------------------------------------------------- helpers

  function parseJSONAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  // Scale factor mapping image pixels -> canvas pixels such that the ROTATED
  // image still covers the whole canvas (same maths as the deck box tool).
  function coverFactor(iw, ih, cw, ch, rot) {
    var a = Math.abs(Math.cos(rot));
    var b = Math.abs(Math.sin(rot));
    var bboxW = iw * a + ih * b;
    var bboxH = iw * b + ih * a;
    return Math.max(cw / bboxW, ch / bboxH);
  }

  function bytesToLabel(b) {
    if (b >= 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB';
    if (b >= 1024) return Math.round(b / 1024) + ' KB';
    return b + ' B';
  }

  function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // ------------------------------------------------------------ constructor

  function PlaymatDesigner(root) {
    this.root = root;
    root.__pmt = this;

    this.matWIn = parseFloat(root.getAttribute('data-mat-w-in'));
    if (isNaN(this.matWIn)) this.matWIn = 24;
    this.matHIn = parseFloat(root.getAttribute('data-mat-h-in'));
    if (isNaN(this.matHIn)) this.matHIn = 14;
    this.safeIn = parseFloat(root.getAttribute('data-safe-in'));
    if (isNaN(this.safeIn)) this.safeIn = 0.5;
    this.ppiGreat = parseFloat(root.getAttribute('data-ppi-great')) || 150;
    this.ppiMin = parseFloat(root.getAttribute('data-ppi-min')) || 100;
    this.maxBytes = (parseFloat(root.getAttribute('data-max-mb')) || 19) * 1024 * 1024;
    this.artSat = parseFloat(root.getAttribute('data-art-sat'));
    if (isNaN(this.artSat)) this.artSat = 1.25;

    this.optionNames = parseJSONAttr(root, 'data-option-names', []);
    this.zoneOptionName = root.getAttribute('data-zone-option') || 'Add Playzones';
    this.finishOptionName = root.getAttribute('data-finish-option') || 'Add Stitched Edging & Playmat thickness';
    this.tubeOptionName = root.getAttribute('data-tube-option') || 'Add a Playmat Tube';
    this.defaultFinish = root.getAttribute('data-default-finish') || '';
    this.defaultTube = root.getAttribute('data-default-tube') || '';

    this.requireArtwork = root.getAttribute('data-require-artwork') === 'true';
    this.hideOriginal = root.getAttribute('data-hide-original') === 'true';
    this.adoptBuy = root.getAttribute('data-adopt-buy') === 'true';
    this.movePaymentTerms = root.getAttribute('data-move-terms') === 'true';
    this.moneySample = root.getAttribute('data-money-sample') || '$1.00';
    this.safeNote = root.getAttribute('data-safe-note') || '';
    this.labels = {
      zoneGroup: root.getAttribute('data-zone-label') || 'Playzone overlay',
      zoneHint: root.getAttribute('data-zone-hint') || '',
      edgeGroup: root.getAttribute('data-edge-label') || 'Edge finish',
      edgeNone: root.getAttribute('data-edge-none') || 'No stitching',
      edgeStitch: root.getAttribute('data-edge-stitch') || 'Stitched edge',
      thickGroup: root.getAttribute('data-thick-label') || 'Thickness',
      thick2: root.getAttribute('data-thick-2') || 'Standard · 2 mm',
      thick3: root.getAttribute('data-thick-3') || 'Thick · 3 mm',
      tubeGroup: root.getAttribute('data-tube-label') || 'Playmat tube',
      zoneColor: root.getAttribute('data-zone-color-label') || 'Line colour'
    };
    this.showTubeDelta = root.getAttribute('data-tube-delta') !== 'false';

    this.zoneMap = parseJSONAttr(root, 'data-zone-map', {});
    this.zoneImages = {};        // value -> HTMLImageElement (lazy)

    this.variants = null;
    var vnode = root.querySelector('[data-pmt-variants-json]');
    if (vnode) { try { this.variants = JSON.parse(vnode.textContent); } catch (e) { this.variants = []; } }
    this.variants = this.variants || [];

    this.state = {
      img: null,
      file: null,
      tx: 0,          // inches offset from mat centre
      ty: 0,
      scale: 1,
      rot: 0,         // radians, driven by the Rotate slider
      zone: null,     // real option values, read from the hidden picker
      finish: null,
      tube: null,
      zoneColor: '#ffffff'   // playzone line colour — white is what ships by default
    };
    this.viewMode = 'table';
    this.pointers = {};
    this.dragging = false;

    this.cacheDom();
    this.bindUI();
    this.watchVariant();

    // Core first, each optional enhancement isolated: a throw in any one of
    // these must never take out selection, pricing or the cart.
    var self = this;
    function step(name, fn) {
      try { fn.call(self); }
      catch (err) { console.error('[playmat-designer] ' + name + ' failed (continuing)', err); }
    }
    step('applyDefaults', this.applyDefaults);
    step('syncFromVariantPicker', this.syncFromVariantPicker);
    step('syncTabs', this.syncTabs);
    step('hideOriginalPicker', this.hideOriginalPicker);
    step('adoptBuyButtons', this.adoptBuyButtons);
    step('buildOptionProxy', this.buildOptionProxy);
    step('updatePrice', this.updatePrice);
    step('ensureFormInputs', this.ensureFormInputs);
    step('wireAtc', this.wireAtc);
    step('updateGate', this.updateGate);
    step('bindBuyGuard', this.bindBuyGuard);
    step('alignPriceToButtons', this.alignPriceToButtons);
    setTimeout(function () { self.alignPriceToButtons(); }, 800);
    setTimeout(function () { self.alignPriceToButtons(); }, 2500);
    step('adoptPaymentTerms', this.adoptPaymentTerms);

    this.render2D();
    this.updatePreviewState();
  }

  PlaymatDesigner.prototype.cacheDom = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    this.el = {
      view: q('[data-pmt-view]'),
      canvasWrap: q('.pmt-canvas-wrap'),
      fileInput: q('[data-pmt-file]'),
      dropzone: q('[data-pmt-dropzone]'),
      replace: q('[data-pmt-replace]'),
      remove: q('[data-pmt-remove]'),
      filename: q('[data-pmt-filename]'),
      dpi: q('[data-pmt-dpi]'),
      zoom: q('[data-pmt-zoom]'),
      rotate: q('[data-pmt-rotate]'),
      status: q('[data-pmt-status]'),
      options: q('[data-pmt-options]'),
      stage: q('[data-pmt-stage]'),
      mat: q('[data-pmt-mat]'),
      matCanvas: q('[data-pmt-mat-canvas]'),
      badgeEdge: q('[data-pmt-badge-edge]'),
      badgeThick: q('[data-pmt-badge-thick]'),
      viewBtns: r.querySelectorAll('[data-pmt-viewmode]'),
      tabs: r.querySelectorAll('[data-pmt-tab]'),
      panes: r.querySelectorAll('[data-pmt-pane]'),
      priceNow: q('[data-pmt-price-now]'),
      priceWas: q('[data-pmt-price-was]'),
      priceNote: q('[data-pmt-price-note]'),
      atc: q('[data-pmt-atc]'),
      buySlot: q('[data-pmt-buy-slot]')
    };
  };

  // ---------------------------------------------------- finish value parsing

  /**
   * The product stores edge + thickness as ONE option with four combined
   * values. The tool presents them as two independent chip groups and
   * recombines the pair into the matching real value. Parsing is by pattern,
   * not by exact string, so admin wording tweaks don't break the split.
   */
  PlaymatDesigner.prototype.parseFinish = function (value) {
    var v = String(value || '');
    return {
      stitched: !/no\s*stitch/i.test(v),
      thick: /3\s*mm|thick/i.test(v)
    };
  };

  PlaymatDesigner.prototype.finishValueFor = function (stitched, thick) {
    var groups = this.readRealOptions();
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].name !== this.finishOptionName) continue;
      for (var j = 0; j < groups[i].values.length; j++) {
        var f = this.parseFinish(groups[i].values[j].value);
        if (f.stitched === stitched && f.thick === thick) return groups[i].values[j].value;
      }
    }
    return null;
  };

  // ---------------------------------------------------------------- defaults

  /**
   * Mert's chosen defaults: No stitching, 2 mm, No tube. Shopify lands on the
   * product's first variant, which is none of those — so the tool selects them
   * once on load. A URL that pins a ?variant= wins: that customer followed a
   * link to a specific configuration and must see exactly that.
   */
  PlaymatDesigner.prototype.applyDefaults = function () {
    if (/[?&]variant=/.test(window.location.search)) return;
    if (this.defaultFinish) this.chooseOption(this.finishOptionName, this.defaultFinish);
    if (this.defaultTube) this.chooseOption(this.tubeOptionName, this.defaultTube);
  };

  // ------------------------------------------------------------- 2D editor

  PlaymatDesigner.prototype.zoneImage = function (value) {
    if (!value || !this.zoneMap || !this.zoneMap[value]) return null;
    var cached = this.zoneImages[value];
    if (cached) return cached.complete && cached.naturalWidth ? cached : null;
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () { self.render2D(); };
    img.src = this.zoneMap[value];
    this.zoneImages[value] = img;
    return null;
  };

  /**
   * The overlay in the customer's chosen line colour. The zone assets are
   * pure white line art, so a source-in composite onto an offscreen canvas
   * recolours every mark at once. Cached per asset + colour; white returns
   * the image untouched.
   */
  PlaymatDesigner.prototype.tintedZone = function (img) {
    var color = String(this.state.zoneColor || '#ffffff').toLowerCase();
    if (color === '#ffffff' || color === '#fff' || color === 'white') return img;
    var key = img.src + '|' + color;
    if (this._tint && this._tint.key === key) return this._tint.canvas;
    try {
      var canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 2400;
      canvas.height = img.naturalHeight || 1400;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      this._tint = { key: key, canvas: canvas };
      return canvas;
    } catch (e) {
      return img;   // tainted or out of memory: white overlay beats none
    }
  };

  PlaymatDesigner.prototype.setZoneColor = function (color) {
    if (!color || this.state.zoneColor === color) return;
    this.state.zoneColor = color;
    this.render2D();
    this.updateNote();
    this.syncZoneColorInputs();
  };

  PlaymatDesigner.prototype.render2D = function () {
    var view = this.el.view;
    if (!view) return;
    var cssW = view.clientWidth || 480;
    var cssH = view.clientHeight || Math.round(cssW * this.matHIn / this.matWIn);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cw = Math.round(cssW * dpr);
    var ch = Math.round(cssH * dpr);
    if (view.width !== cw) view.width = cw;
    if (view.height !== ch) view.height = ch;

    var ctx = view.getContext('2d');
    ctx.clearRect(0, 0, cw, ch);

    // base
    ctx.fillStyle = '#10142c';
    ctx.fillRect(0, 0, cw, ch);

    var st = this.state;
    this.lastPPI = null;

    if (st.img) {
      var iw = st.img.naturalWidth, ih = st.img.naturalHeight;
      var k = coverFactor(iw, ih, cw, ch, st.rot) * st.scale;  // cover fit x zoom
      ctx.save();
      if (ctx.filter !== undefined && this.artSat !== 1) {
        ctx.filter = 'saturate(' + this.artSat + ')';
      }
      ctx.imageSmoothingQuality = 'high';
      ctx.translate(cw / 2 + st.tx * (cw / this.matWIn), ch / 2 + st.ty * (cw / this.matWIn));
      ctx.rotate(st.rot);
      ctx.drawImage(st.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
      ctx.restore();

      // effective print resolution: source pixels per printed inch
      this.lastPPI = cw / (k * this.matWIn) * (this.srcScale || 1);
    }

    // playzone line art, printed edge to edge over the artwork, in the
    // customer's chosen line colour
    var zimg = this.zoneImage(st.zone);
    if (zimg) {
      ctx.drawImage(this.tintedZone(zimg), 0, 0, cw, ch);
    }

    // The preview mirrors what PRINTS — art and zone only — so the copy is
    // taken now, before the editor-only guides go on.
    this.mirrorToPreview();

    // safe-area guide (print is full bleed; this is guidance, not a trim line)
    var inset = this.safeIn * (cw / this.matWIn);
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.setLineDash([6 * dpr, 5 * dpr]);
    ctx.lineWidth = 1.2 * dpr;
    roundedRectPath(ctx, inset, inset, cw - 2 * inset, ch - 2 * inset, 4 * dpr);
    ctx.stroke();
    ctx.restore();

    // The placement snapshot for the order refreshes after every visual
    // change (debounced) — captured EAGERLY, never at submit time, so the
    // order can never be held up waiting for it.
    this.scheduleShot();

    if (this.safeNote && st.img) {
      ctx.save();
      var fs = 10 * dpr;
      ctx.font = '500 ' + fs + 'px system-ui, sans-serif';
      var pad = 6 * dpr;
      var tw = ctx.measureText(this.safeNote).width;
      var bx = cw - inset - tw - pad * 2 - 6 * dpr;
      var by = ch - inset - fs - pad * 2 - 4 * dpr;
      ctx.fillStyle = 'rgba(10,14,30,0.65)';
      roundedRectPath(ctx, bx, by, tw + pad * 2, fs + pad * 1.6, (fs + pad * 1.6) / 2);
      ctx.fill();
      ctx.fillStyle = '#dfe8f2';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.safeNote, bx + pad, by + (fs + pad * 1.6) / 2 + dpr * 0.5);
      ctx.restore();
    }

    this.updateReadouts();
  };

  /**
   * Placement snapshot: art, zone overlay AND the dashed safe-area guide —
   * exactly as the customer arranged it, attached to the order as
   * properties[Design Preview] beside the original file. The guides make it
   * self-evidently a reference image, not print art. The cart-page and
   * cart-drawer thumbnail patches already prefer a "Design Preview" property,
   * so cart lines pick this image up automatically.
   *
   * Rendered OFFSCREEN at a fixed 2400x1400 (100px per printed inch of the
   * 24x14in mat — the same space the zone SVGs are authored in), never from
   * the on-screen canvas: the editor canvas is sized to the customer's
   * screen, which made phone-placed orders ship a ~700px reference the print
   * team could not judge placement from.
   *
   * Captured eagerly on a 600ms debounce after each change. If capture ever
   * fails the order still goes through with the artwork alone — a submit path
   * must never wait on an async artifact (hard-won sleeve-tool lesson).
   */
  PlaymatDesigner.prototype.scheduleShot = function () {
    if (!this.state.img) return;
    var self = this;
    clearTimeout(this._shotTimer);
    this._shotTimer = setTimeout(function () { self.captureShot(); }, 600);
  };

  PlaymatDesigner.prototype.captureShot = function () {
    var self = this;
    var st = this.state;
    if (!st.img) return;
    try {
      var c = document.createElement('canvas');
      if (!c.toBlob) return;
      var cw = 2400;
      var ch = Math.round(cw * this.matHIn / this.matWIn);
      c.width = cw;
      c.height = ch;
      var ctx = c.getContext('2d');

      // Same drawing as render2D, at print-reference scale: base, art,
      // tinted zone overlay, safe-area guide. The on-screen hint pill is
      // deliberately left out — it is UI, not placement information.
      ctx.fillStyle = '#10142c';
      ctx.fillRect(0, 0, cw, ch);

      var iw = st.img.naturalWidth, ih = st.img.naturalHeight;
      var k = coverFactor(iw, ih, cw, ch, st.rot) * st.scale;
      ctx.save();
      if (ctx.filter !== undefined && this.artSat !== 1) {
        ctx.filter = 'saturate(' + this.artSat + ')';
      }
      ctx.imageSmoothingQuality = 'high';
      ctx.translate(cw / 2 + st.tx * (cw / this.matWIn), ch / 2 + st.ty * (cw / this.matWIn));
      ctx.rotate(st.rot);
      ctx.drawImage(st.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
      ctx.restore();

      var zimg = this.zoneImage(st.zone);
      if (zimg) ctx.drawImage(this.tintedZone(zimg), 0, 0, cw, ch);

      var g = 2;  // guide weight, matches what a dpr-2 screen shows
      var inset = this.safeIn * (cw / this.matWIn);
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.setLineDash([6 * g, 5 * g]);
      ctx.lineWidth = 1.2 * g;
      roundedRectPath(ctx, inset, inset, cw - 2 * inset, ch - 2 * inset, 4 * g);
      ctx.stroke();
      ctx.restore();

      c.toBlob(function (blob) {
        if (!blob || !self.state.img) return;
        self.shotFile = new File([blob], 'placement-preview.jpg', { type: 'image/jpeg' });
        var form = self.form || self.findForm();
        var input = form && form.querySelector('[data-pmt-prop="shot"]');
        if (input) self.setFileOnInput(input, self.shotFile);
      }, 'image/jpeg', 0.9);
    } catch (e) { /* best-effort only */ }
  };

  /** The preview mat shows exactly what the editor canvas shows. */
  PlaymatDesigner.prototype.mirrorToPreview = function () {
    var src = this.el.view, dst = this.el.matCanvas;
    if (!src || !dst) return;
    if (dst.width !== src.width) dst.width = src.width;
    if (dst.height !== src.height) dst.height = src.height;
    var ctx = dst.getContext('2d');
    ctx.clearRect(0, 0, dst.width, dst.height);
    ctx.drawImage(src, 0, 0);
  };

  PlaymatDesigner.prototype.updateReadouts = function () {
    var dpi = this.el.dpi;
    if (!dpi) return;
    if (this.lastPPI) {
      var p = Math.round(this.lastPPI);
      var cls = p >= this.ppiGreat ? '' : (p >= this.ppiMin ? 'pmt-mid' : 'pmt-warn');
      var word = p >= this.ppiGreat ? 'great' : (p >= this.ppiMin ? 'good' : 'low — aim for ' + this.ppiGreat + '+');
      dpi.textContent = p + ' PPI — ' + word;
      dpi.classList.remove('pmt-mid', 'pmt-warn');
      if (cls) dpi.classList.add(cls);
      dpi.hidden = false;
    } else {
      dpi.hidden = true;
    }
  };

  // ---------------------------------------------------------------- upload

  PlaymatDesigner.prototype.loadFile = function (file) {
    var self = this;
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return this.say('That file is not an image. Please upload a JPG or PNG.', true);
    }
    if (file.size > this.maxBytes) {
      return this.say('That file is ' + bytesToLabel(file.size) + '. Please keep it under ' +
        bytesToLabel(this.maxBytes) + '.', true);
    }

    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      // Downscale oversized images for smooth canvas work; the PPI meter is
      // corrected by srcScale so it reports the ORIGINAL file's resolution.
      // The customer's original file is what goes to the cart, untouched.
      var MAXDIM = 2600;
      var big = Math.max(img.naturalWidth, img.naturalHeight);
      if (big > MAXDIM) {
        var r = MAXDIM / big;
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * r);
        c.height = Math.round(img.naturalHeight * r);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var small = new Image();
        small.onload = function () {
          URL.revokeObjectURL(url);
          self.srcScale = 1 / r;
          self.adopt(small, file);
        };
        small.src = c.toDataURL('image/jpeg', 0.92);
      } else {
        self.srcScale = 1;
        self.adopt(img, file);
      }
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('That image could not be read. Try re-saving it as a JPG or PNG.', true);
    };
    img.src = url;
  };

  PlaymatDesigner.prototype.adopt = function (img, file) {
    this.state.img = img;
    this.state.file = file;
    this.state.tx = 0; this.state.ty = 0; this.state.scale = 1; this.state.rot = 0;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    if (this.el.filename) this.el.filename.textContent = file.name + ' — ' + bytesToLabel(file.size);
    this.root.classList.add('pmt--has-art');
    this.root.classList.remove('pmt--no-art');
    this.say('');
    this.render2D();
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  PlaymatDesigner.prototype.clearArt = function () {
    this.state.img = null;
    this.state.file = null;
    this.shotFile = null;
    clearTimeout(this._shotTimer);
    this.srcScale = 1;
    this.state.rot = 0; this.state.scale = 1; this.state.tx = 0; this.state.ty = 0;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    this.root.classList.remove('pmt--has-art');
    this.root.classList.add('pmt--no-art');
    if (this.el.filename) this.el.filename.textContent = '';
    if (this.el.fileInput) this.el.fileInput.value = '';
    this.render2D();
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  PlaymatDesigner.prototype.say = function (msg, isError) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('pmt-warn', !!isError);
    this.el.status.hidden = !msg;
  };

  // ------------------------------------------------------------ interaction

  PlaymatDesigner.prototype.bindUI = function () {
    var self = this;
    var el = this.el;

    if (el.fileInput) {
      el.fileInput.addEventListener('change', function () {
        self.loadFile(this.files && this.files[0]);
      });
    }
    if (el.replace) {
      el.replace.addEventListener('click', function () {
        if (el.fileInput) el.fileInput.click();
      });
    }
    if (el.remove) {
      el.remove.addEventListener('click', function () { self.clearArt(); });
    }

    // Zoom + Rotate sliders, same behaviour as the deck box tool. Zoom's floor
    // is 100%: the print runs edge to edge, so the art may never fall short of
    // covering the whole mat.
    if (el.zoom) {
      el.zoom.addEventListener('input', function () {
        self.state.scale = clamp(parseFloat(this.value) / 100, 1, 4);
        self.render2D();
        self.updateNote();
      });
    }
    if (el.rotate) {
      el.rotate.addEventListener('input', function () {
        self.state.rot = parseFloat(this.value) * Math.PI / 180;
        self.render2D();
        self.updateNote();
      });
    }

    if (el.dropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.add('pmt-drop-hot');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.remove('pmt-drop-hot');
        });
      });
      el.dropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) self.loadFile(e.dataTransfer.files[0]);
      });
    }

    // drag to reposition, wheel / pinch to zoom — no sliders needed
    if (el.view) {
      var last = null;
      el.view.addEventListener('pointerdown', function (e) {
        if (!self.state.img) return;
        el.view.setPointerCapture(e.pointerId);
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        last = { x: e.clientX, y: e.clientY };
        self.dragging = true;
      });
      el.view.addEventListener('pointermove', function (e) {
        if (!self.dragging || !self.state.img) return;
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var ids = Object.keys(self.pointers);
        if (ids.length >= 2) {
          var a = self.pointers[ids[0]], b = self.pointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y);
          if (self.pinchBase) {
            self.state.scale = clamp(self.pinchScale * (d / self.pinchBase), 1, 4);
            if (el.zoom) el.zoom.value = String(Math.round(self.state.scale * 100));
          } else {
            self.pinchBase = d; self.pinchScale = self.state.scale;
          }
          self.render2D();
          return;
        }
        var inPerPx = self.matWIn / (el.view.clientWidth || 480);
        self.state.tx = clamp(self.state.tx + (e.clientX - last.x) * inPerPx, -self.matWIn, self.matWIn);
        self.state.ty = clamp(self.state.ty + (e.clientY - last.y) * inPerPx, -self.matHIn, self.matHIn);
        last = { x: e.clientX, y: e.clientY };
        self.render2D();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        el.view.addEventListener(evt, function (e) {
          delete self.pointers[e.pointerId];
          if (!Object.keys(self.pointers).length) {
            self.dragging = false; self.pinchBase = null;
            self.updateNote();
          }
        });
      });
      el.view.addEventListener('wheel', function (e) {
        if (!self.state.img) return;
        e.preventDefault();
        self.state.scale = clamp(self.state.scale * (e.deltaY < 0 ? 1.06 : 0.94), 1, 4);
        if (el.zoom) el.zoom.value = String(Math.round(self.state.scale * 100));
        self.render2D();
        self.updateNote();
      }, { passive: false });
      el.view.addEventListener('dblclick', function () {
        if (!self.state.img) return;
        self.state.tx = 0; self.state.ty = 0; self.state.scale = 1; self.state.rot = 0;
        if (el.zoom) el.zoom.value = '100';
        if (el.rotate) el.rotate.value = '0';
        self.render2D();
        self.updateNote();
      });
    }

    if (el.viewBtns) {
      Array.prototype.forEach.call(el.viewBtns, function (btn) {
        btn.addEventListener('click', function () {
          self.setViewMode(btn.getAttribute('data-pmt-viewmode'));
        });
      });
    }

    for (var i = 0; i < el.tabs.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          self.setTab(btn.getAttribute('data-pmt-tab'));
        });
      })(el.tabs[i]);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(function () {
      self.syncTabs();
      self.render2D();
    }) : null;
    if (ro) ro.observe(this.root);
    window.addEventListener('resize', function () { self.syncTabs(); self.alignPriceToButtons(); });
  };

  PlaymatDesigner.prototype.setViewMode = function (mode) {
    this.viewMode = mode === 'flat' ? 'flat' : 'table';
    if (this.el.stage) this.el.stage.classList.toggle('pmt--flat', this.viewMode === 'flat');
    if (this.el.viewBtns) {
      Array.prototype.forEach.call(this.el.viewBtns, function (btn) {
        var on = btn.getAttribute('data-pmt-viewmode') === (mode === 'flat' ? 'flat' : 'table');
        btn.classList.toggle('pmt-view--on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  };

  // ------------------------------------------------------------------ tabs

  PlaymatDesigner.prototype.setTab = function (name) {
    var el = this.el, i;
    this.currentTab = name;
    for (i = 0; i < el.tabs.length; i++) {
      var t = el.tabs[i];
      var on = t.getAttribute('data-pmt-tab') === name;
      t.classList.toggle('pmt-tab--on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (i = 0; i < el.panes.length; i++) {
      var p = el.panes[i];
      p.hidden = p.getAttribute('data-pmt-pane') !== name;
    }
    this.render2D();
  };

  PlaymatDesigner.prototype.syncTabs = function () {
    var stacked = window.matchMedia
      ? window.matchMedia('(max-width: ' + STACK_BREAKPOINT + 'px)').matches
      : window.innerWidth <= STACK_BREAKPOINT;
    if (stacked) {
      if (!this.stacked) {
        this.stacked = true;
        this.setTab(this.currentTab || 'design');
      }
    } else if (this.stacked !== false) {
      this.stacked = false;
      for (var i = 0; i < this.el.panes.length; i++) this.el.panes[i].hidden = false;
      this.render2D();
    }
  };

  // --------------------------------------------- real option controls (Metro)

  PlaymatDesigner.prototype.findForm = function () {
    // Related products and quick-add drawers render their own /cart/add forms,
    // so an unscoped querySelector can grab the wrong one. Two homes are
    // trusted: the main product wrapper, and THIS TOOL — after adoptBuyButtons
    // the real form lives inside the buy bar, at which point the main-product
    // preference no longer matches and plain document order would hand back
    // whichever quick-view form happens to come first.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var fallback = null;
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (!f.querySelector('[name="id"]')) continue;
      if (!fallback) fallback = f;
      if (f.closest('.main-product-view, #MainProduct, [data-section-type="main-product"]')) {
        return f;
      }
      if (this.root && this.root.contains(f)) return f;
    }
    return fallback;
  };

  PlaymatDesigner.prototype.productScope = function () {
    var form = this.findForm();
    var scope = form && form.closest
      ? form.closest('product-info, .main-product-view, #MainProduct')
      : null;
    if (!scope) {
      scope = document.querySelector('product-info') ||
              document.querySelector('.main-product-view, #MainProduct');
    }
    return scope || document;
  };

  PlaymatDesigner.prototype.realControlsFor = function (name) {
    var esc = String(name).replace(/"/g, '\\"');
    var sel = 'input[type="radio"][name="' + esc + '"],' +
              'input[type="radio"][name="options[' + esc + ']"],' +
              'select[name="options[' + esc + ']"],' +
              'select[name="' + esc + '"]';
    return this.productScope().querySelectorAll(sel);
  };

  PlaymatDesigner.prototype.readRealOptions = function () {
    var names = this.optionNames || [];
    var out = [];
    for (var i = 0; i < names.length; i++) {
      var nodes = this.realControlsFor(names[i]);
      if (!nodes.length) continue;
      var group = { name: names[i], values: [], selected: null };
      var seen = {};
      var add = function (value, checked, disabled) {
        var key = '@' + value;
        if (seen[key]) {
          if (checked) { seen[key].checked = true; group.selected = value; }
          if (!disabled) seen[key].disabled = false;
          return;
        }
        seen[key] = { value: value, checked: !!checked, disabled: !!disabled };
        group.values.push(seen[key]);
        if (checked) group.selected = value;
      };
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (n.tagName === 'SELECT') {
          for (var k = 0; k < n.options.length; k++) {
            var o = n.options[k];
            add(o.value, o.selected, o.disabled);
          }
        } else {
          var dis = n.disabled || n.classList.contains('variant-input-disabled') ||
                    (n.parentNode && n.parentNode.classList &&
                     n.parentNode.classList.contains('variant-disabled'));
          add(n.value, n.checked, dis);
        }
      }
      out.push(group);
    }
    return out;
  };

  /**
   * Forwards a choice to the real control. product-info.js does
   * `if (!this.contains(event.target)) return;` — it ignores option changes
   * whose target is outside <product-info>, so the proxy CLICKS the real
   * input rather than re-creating the picker somewhere Metro won't listen.
   */
  PlaymatDesigner.prototype.chooseOption = function (name, value) {
    var nodes = this.realControlsFor(name);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.tagName === 'SELECT') {
        if (n.value === value) return;
        n.value = value;
        n.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (n.value === value) {
        if (n.checked) return;
        n.click();
        return;
      }
    }
  };

  PlaymatDesigner.prototype.syncFromVariantPicker = function () {
    var groups = this.readRealOptions();
    var vals = {};
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].selected != null) vals[groups[i].name] = groups[i].selected;
    }
    this.applyOptionValues(vals);
    return vals;
  };

  PlaymatDesigner.prototype.applyOptionValues = function (vals) {
    var changed = false;
    var zone = vals[this.zoneOptionName];
    var finish = vals[this.finishOptionName];
    var tube = vals[this.tubeOptionName];
    if (zone !== undefined && zone !== this.state.zone) { this.state.zone = zone; changed = true; }
    if (finish !== undefined && finish !== this.state.finish) { this.state.finish = finish; changed = true; }
    if (tube !== undefined && tube !== this.state.tube) { this.state.tube = tube; changed = true; }
    if (changed) {
      this.render2D();
      this.updatePreviewState();
      this.updateNote();
    }
    return changed;
  };

  PlaymatDesigner.prototype.applyVariant = function (variant) {
    if (!variant || !variant.options) return this.syncFromVariantPicker();
    var vals = {};
    var names = this.optionNames || [];
    for (var i = 0; i < variant.options.length; i++) {
      if (names[i]) vals[names[i]] = variant.options[i];
    }
    if (!Object.keys(vals).length) return this.syncFromVariantPicker();
    this.applyOptionValues(vals);
  };

  PlaymatDesigner.prototype.watchVariant = function () {
    var self = this;
    function afterChange() {
      self.ensureFormInputs(); self.wireAtc(); self.updateGate();
      self.hideOriginalPicker(); self.adoptBuyButtons(); self.adoptPaymentTerms();
      self.alignPriceToButtons();
      self.refreshOptionProxy(); self.updatePrice();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
          var v = evt && evt.data && evt.data.variant;
          self.applyVariant(v);
          setTimeout(afterChange, 0);
        });
      } catch (e) { /* fall through to DOM listeners */ }
    }

    document.addEventListener('variant:change', function (e) {
      self.applyVariant(e.detail && e.detail.variant);
      setTimeout(afterChange, 0);
    });

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      var names = self.optionNames || [];
      var hit = t.name.indexOf('options[') === 0;
      for (var i = 0; i < names.length && !hit; i++) {
        if (t.name === names[i]) hit = true;
      }
      if (!hit) return;
      self.syncFromVariantPicker();
      setTimeout(afterChange, 60);
    });
  };

  // ------------------------------------------------------------ option proxy

  /**
   * The three groups, in the approved layout: playzone dropdown (12 values is
   * too many for chips), then edge + thickness side by side (the split of the
   * combined option), then the tube chips with No tube presented first.
   */
  PlaymatDesigner.prototype.buildOptionProxy = function () {
    var host = this.el.options;
    if (!host) return;
    host.textContent = '';
    host.hidden = true;

    var groups = this.readRealOptions();
    if (!groups.length) return;
    var byName = {};
    groups.forEach(function (g) { byName[g.name] = g; });
    var self = this;

    // ---- playzone dropdown
    var zoneGroup = byName[this.zoneOptionName];
    if (zoneGroup) {
      var zrow = document.createElement('div');
      zrow.className = 'pmt-opt';
      var zlab = document.createElement('span');
      zlab.className = 'pmt-opt-label';
      zlab.textContent = this.labels.zoneGroup;
      zrow.appendChild(zlab);
      var sel = document.createElement('select');
      sel.className = 'pmt-zone-select';
      sel.setAttribute('data-pmt-zone-select', '');
      sel.setAttribute('aria-label', this.labels.zoneGroup);
      zoneGroup.values.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.value;
        opt.textContent = v.value;
        if (v.checked) opt.selected = true;
        if (v.disabled) opt.disabled = true;
        sel.appendChild(opt);
      });
      sel.addEventListener('change', function () {
        self.chooseOption(self.zoneOptionName, sel.value);
      });
      zrow.appendChild(sel);
      zrow.appendChild(this.buildZoneColorRow());
      if (this.labels.zoneHint) {
        var hint = document.createElement('p');
        hint.className = 'pmt-hint';
        hint.setAttribute('data-pmt-zone-hint', '');
        hint.textContent = this.labels.zoneHint;
        zrow.appendChild(hint);
      }
      host.appendChild(zrow);
    }

    // ---- edge + thickness, one row
    var finishGroup = byName[this.finishOptionName];
    if (finishGroup) {
      var pair = document.createElement('div');
      pair.className = 'pmt-opt-pair';
      pair.appendChild(this.chipGroup('edge', this.labels.edgeGroup, [
        { key: 'none', label: this.labels.edgeNone },
        { key: 'stitch', label: this.labels.edgeStitch }
      ]));
      pair.appendChild(this.chipGroup('thick', this.labels.thickGroup, [
        { key: '2', label: this.labels.thick2 },
        { key: '3', label: this.labels.thick3 }
      ]));
      host.appendChild(pair);
    }

    // ---- tube, No tube first (Mert's ordering)
    var tubeGroup = byName[this.tubeOptionName];
    if (tubeGroup) {
      var values = tubeGroup.values.slice().sort(function (a, b) {
        var an = /no\s*tube/i.test(a.value) ? 0 : 1;
        var bn = /no\s*tube/i.test(b.value) ? 0 : 1;
        return an - bn;
      });
      var trow = document.createElement('div');
      trow.className = 'pmt-opt';
      var tlab = document.createElement('span');
      tlab.className = 'pmt-opt-label';
      tlab.textContent = this.labels.tubeGroup;
      trow.appendChild(tlab);
      var tul = document.createElement('div');
      tul.className = 'pmt-chips';
      values.forEach(function (v) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pmt-chip';
        btn.setAttribute('data-pmt-tube', v.value);
        var span = document.createElement('span');
        span.textContent = v.value;
        span.style.color = 'inherit';
        btn.appendChild(span);
        if (!/no\s*tube/i.test(v.value)) {
          var delta = document.createElement('span');
          delta.className = 'pmt-chip-delta';
          delta.setAttribute('data-pmt-tube-delta', '');
          btn.appendChild(delta);
        }
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        btn.addEventListener('click', function () {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          self.chooseOption(self.tubeOptionName, v.value);
        });
        tul.appendChild(btn);
      });
      trow.appendChild(tul);
      host.appendChild(trow);
    }

    host.hidden = false;
    this.refreshOptionProxy();
  };

  /**
   * The line-colour row under the zone dropdown: six presets plus a free
   * picker, so the zone lines can be matched to the artwork. Hidden unless a
   * zone with overlay art is selected (it means nothing on a clean mat).
   */
  PlaymatDesigner.prototype.buildZoneColorRow = function () {
    var self = this;
    var row = document.createElement('div');
    row.className = 'pmt-zone-colors';
    row.setAttribute('data-pmt-zone-colors', '');
    row.hidden = true;
    var lab = document.createElement('span');
    lab.className = 'pmt-zone-color-label';
    lab.textContent = this.labels.zoneColor;
    row.appendChild(lab);
    ['#ffffff', '#0e0e11', '#f5c542', '#e23d3d', '#3aa0ff', '#c168ff'].forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pmt-swatch';
      btn.setAttribute('data-pmt-zone-color', c);
      btn.setAttribute('aria-label', self.labels.zoneColor + ' ' + c);
      btn.style.background = c;
      btn.addEventListener('click', function () { self.setZoneColor(c); });
      row.appendChild(btn);
    });
    var custom = document.createElement('label');
    custom.className = 'pmt-swatch pmt-swatch--custom';
    custom.title = this.labels.zoneColor;
    var input = document.createElement('input');
    input.type = 'color';
    input.value = this.state.zoneColor || '#ffffff';
    input.setAttribute('data-pmt-zone-color-input', '');
    input.setAttribute('aria-label', this.labels.zoneColor);
    input.addEventListener('input', function () { self.setZoneColor(input.value); });
    custom.appendChild(input);
    row.appendChild(custom);
    return row;
  };

  /** Mirrors state.zoneColor onto the swatches and the free picker. */
  PlaymatDesigner.prototype.syncZoneColorInputs = function () {
    var root = this.root;
    var color = String(this.state.zoneColor || '#ffffff').toLowerCase();
    var btns = root.querySelectorAll('[data-pmt-zone-color]');
    var preset = false;
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-pmt-zone-color').toLowerCase() === color;
      btns[i].classList.toggle('pmt-swatch--on', on);
      btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) preset = true;
    }
    var input = root.querySelector('[data-pmt-zone-color-input]');
    if (input) {
      if (input.value.toLowerCase() !== color) input.value = color;
      var wrap = input.closest('.pmt-swatch--custom');
      if (wrap) wrap.classList.toggle('pmt-swatch--on', !preset);
    }
    var form = this.form || this.findForm();
    var prop = form && form.querySelector('[data-pmt-prop="zonecolor"]');
    if (prop) {
      var val = this.zoneColorProperty();
      prop.value = val;
      prop.disabled = !val;   // a disabled input never submits an empty property
    }
  };

  /** The order property: only meaningful with an overlay on the mat. */
  PlaymatDesigner.prototype.zoneColorProperty = function () {
    var z = this.state.zone;
    if (!z || /no\s*playzone/i.test(z)) return '';
    var c = String(this.state.zoneColor || '#ffffff').toLowerCase();
    return c === '#ffffff' ? '' : c;
  };

  PlaymatDesigner.prototype.chipGroup = function (kind, label, chips) {
    var self = this;
    var row = document.createElement('div');
    row.className = 'pmt-opt';
    var lab = document.createElement('span');
    lab.className = 'pmt-opt-label';
    lab.textContent = label;
    row.appendChild(lab);
    var ul = document.createElement('div');
    ul.className = 'pmt-chips';
    chips.forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pmt-chip';
      btn.textContent = c.label;
      btn.setAttribute('data-pmt-' + kind, c.key);
      btn.addEventListener('click', function () {
        var f = self.parseFinish(self.state.finish);
        var stitched = kind === 'edge' ? c.key === 'stitch' : f.stitched;
        var thick = kind === 'thick' ? c.key === '3' : f.thick;
        var value = self.finishValueFor(stitched, thick);
        if (value) self.chooseOption(self.finishOptionName, value);
      });
      ul.appendChild(btn);
    });
    row.appendChild(ul);
    return row;
  };

  /** Mirrors the real picker's state back onto the proxy controls. */
  PlaymatDesigner.prototype.refreshOptionProxy = function () {
    var root = this.root;
    var groups = this.readRealOptions();
    var byName = {};
    groups.forEach(function (g) { byName[g.name] = g; });

    var zoneGroup = byName[this.zoneOptionName];
    var sel = root.querySelector('[data-pmt-zone-select]');
    if (sel && zoneGroup && zoneGroup.selected != null && sel.value !== zoneGroup.selected) {
      sel.value = zoneGroup.selected;
    }

    var f = this.parseFinish(this.state.finish);
    var mark = function (attr, key) {
      var btns = root.querySelectorAll('[' + attr + ']');
      for (var i = 0; i < btns.length; i++) {
        var on = btns[i].getAttribute(attr) === key;
        btns[i].classList.toggle('pmt-chip--on', on);
        btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    };
    if (this.state.finish != null) {
      mark('data-pmt-edge', f.stitched ? 'stitch' : 'none');
      mark('data-pmt-thick', f.thick ? '3' : '2');
    }
    if (this.state.tube != null) {
      mark('data-pmt-tube', this.state.tube);
    }

    this.updateTubeDelta();
    this.updateZoneHint();
    this.syncZoneColorInputs();
  };

  /** "+$20.50" on the tube chip: real price difference at the current combo. */
  PlaymatDesigner.prototype.updateTubeDelta = function () {
    var el = this.root.querySelector('[data-pmt-tube-delta]');
    if (!el) return;
    if (!this.showTubeDelta) { el.textContent = ''; return; }
    var names = this.optionNames || [];
    var zi = names.indexOf(this.zoneOptionName);
    var fi = names.indexOf(this.finishOptionName);
    var ti = names.indexOf(this.tubeOptionName);
    if (ti < 0) { el.textContent = ''; return; }
    var withT = null, without = null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (zi >= 0 && this.state.zone != null && v.o[zi] !== this.state.zone) continue;
      if (fi >= 0 && this.state.finish != null && v.o[fi] !== this.state.finish) continue;
      if (/no\s*tube/i.test(v.o[ti])) { without = v; } else { withT = v; }
    }
    if (withT && without && withT.c > without.c) {
      el.textContent = ' +' + this.formatCents(withT.c - without.c);
    } else {
      el.textContent = '';
    }
  };

  /** The zone hint doubles as the "overlay art pending" notice. */
  PlaymatDesigner.prototype.updateZoneHint = function () {
    var z = this.state.zone;
    var noZone = !z || /no\s*playzone/i.test(z);
    var colors = this.root.querySelector('[data-pmt-zone-colors]');
    if (colors) colors.hidden = noZone || !(this.zoneMap && this.zoneMap[z]);
    var hint = this.root.querySelector('[data-pmt-zone-hint]');
    if (!hint) return;
    if (!noZone && (!this.zoneMap || !this.zoneMap[z])) {
      hint.textContent = z + ' prints on your mat — its preview overlay is being added, so the preview shows the plain artwork for now.';
    } else {
      hint.textContent = this.labels.zoneHint;
    }
  };

  /** Stage classes + badges reflect the selected finish. */
  PlaymatDesigner.prototype.updatePreviewState = function () {
    var f = this.parseFinish(this.state.finish);
    if (this.el.stage) {
      this.el.stage.classList.toggle('pmt--stitched', this.state.finish != null && f.stitched);
      this.el.stage.classList.toggle('pmt--thick', this.state.finish != null && f.thick);
      this.el.stage.classList.toggle('pmt--flat', this.viewMode === 'flat');
    }
    if (this.el.badgeEdge) {
      this.el.badgeEdge.textContent = this.state.finish == null ? '' :
        (f.stitched ? this.labels.edgeStitch : this.labels.edgeNone);
      this.el.badgeEdge.hidden = this.state.finish == null;
    }
    if (this.el.badgeThick) {
      this.el.badgeThick.textContent = this.state.finish == null ? '' : (f.thick ? '3 mm' : '2 mm');
      this.el.badgeThick.hidden = this.state.finish == null;
    }
  };

  PlaymatDesigner.prototype.hideOriginalPicker = function () {
    if (!this.hideOriginal) return;
    var scope = this.productScope();
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('variant-selects, .product-block-variant-picker');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('pmt-visually-hidden')) {
        nodes[i].classList.add('pmt-visually-hidden');
      }
    }
  };

  // ---------------------------------------------------------------- pricing

  PlaymatDesigner.prototype.moneyParts = function () {
    if (this._money) return this._money;
    var m = String(this.moneySample);
    var first = m.search(/[0-9]/);
    var last = m.length - 1 - String(m).split('').reverse().join('').search(/[0-9]/);
    this._money = {
      pre: first > 0 ? m.slice(0, first) : '',
      post: last < m.length - 1 ? m.slice(last + 1) : '',
      comma: /1[.,]00/.test(m) ? m.charAt(m.search(/1[.,]00/) + 1) : '.'
    };
    return this._money;
  };

  PlaymatDesigner.prototype.formatCents = function (cents) {
    var mp = this.moneyParts();
    var whole = Math.floor(cents / 100);
    var frac = String(Math.round(cents % 100));
    while (frac.length < 2) frac = '0' + frac;
    var s = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, mp.comma === ',' ? '.' : ',');
    return mp.pre + s + mp.comma + frac + mp.post;
  };

  PlaymatDesigner.prototype.currentVariant = function () {
    var names = this.optionNames || [];
    var groups = this.readRealOptions();
    var sel = {};
    groups.forEach(function (g) { if (g.selected != null) sel[g.name] = g.selected; });
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < names.length; j++) {
        if (sel[names[j]] !== undefined && v.o[j] !== sel[names[j]]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  PlaymatDesigner.prototype.updatePrice = function () {
    if (this.el.priceNow) {
      var v = this.currentVariant();
      this.el.priceNow.textContent = v ? v.p : '';
      if (this.el.priceWas) {
        var show = v && v.cp && v.cp > v.c;
        this.el.priceWas.textContent = show ? this.formatCents(v.cp) : '';
        this.el.priceWas.hidden = !show;
      }
    }
    if (this.el.priceNote) {
      var f = this.parseFinish(this.state.finish);
      var bits = [];
      if (this.state.zone) bits.push(this.state.zone);
      if (this.state.finish != null) {
        bits.push(f.stitched ? this.labels.edgeStitch : this.labels.edgeNone);
        bits.push(f.thick ? '3 mm' : '2 mm');
      }
      if (this.state.tube) bits.push(this.state.tube);
      this.el.priceNote.textContent = bits.join(' · ');
    }
    this.updateTubeDelta();
  };

  // ------------------------------------------------------------ add to cart

  PlaymatDesigner.prototype.wireAtc = function () {
    var btn = this.el.atc;
    if (!btn) return;
    var form = this.findForm();
    if (!form) return;

    // NOT form.id — the product form contains <input name="id">, which the
    // legacy named-property getter exposes as an own property shadowing the
    // real id accessor. Always getAttribute on a product form.
    var formId = form.getAttribute('id');
    if (!formId) {
      formId = 'PlaymatDesignerProductForm';
      form.setAttribute('id', formId);
    }
    btn.setAttribute('form', formId);

    if (btn.__pmtWired) return;
    btn.__pmtWired = true;
    btn.addEventListener('click', function () {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('pmt-loading');
      setTimeout(function () { btn.classList.remove('pmt-loading'); }, 5000);
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { btn.classList.remove('pmt-loading'); });
        subscribe(PUB_SUB_EVENTS.cartError, function () { btn.classList.remove('pmt-loading'); });
      } catch (e) { /* not fatal */ }
    }
  };

  /**
   * Relocates Metro's own buy-buttons block into the buy bar. Safe to move —
   * unlike the variant picker, product-info.js finds #ProductBuyButtons-… by
   * id document-wide and every reference is null-safe. The containment guard
   * only governs option changes, not submission.
   */
  PlaymatDesigner.prototype.adoptBuyButtons = function () {
    if (!this.adoptBuy) return;
    var slot = this.el.buySlot;
    if (!slot) return;
    var scope = this.productScope();
    var bb = (scope.querySelector && scope.querySelector('[id^="ProductBuyButtons-"]')) ||
             document.querySelector('[id^="ProductBuyButtons-"]');
    if (!bb || slot.contains(bb)) return;
    slot.appendChild(bb);
    this.root.classList.add('pmt--adopted-buy');
  };

  PlaymatDesigner.prototype.adoptPaymentTerms = function () {
    if (!this.movePaymentTerms) return;
    var slot = this.el.buySlot;
    if (!slot) return;

    var terms = this._paymentTerms ||
                document.querySelector('.product-block-payment-installments');
    if (!terms) return;
    this._paymentTerms = terms;

    if (this._termsPoll) { clearInterval(this._termsPoll); this._termsPoll = null; }
    if (!slot.contains(terms)) slot.appendChild(terms);

    function refine() {
      var payBtn = slot.querySelector('.shopify-payment-button');
      if (!payBtn) return false;
      var more = payBtn.querySelector('.shopify-payment-button__more-options');
      if (more) {
        if (terms.parentNode !== payBtn || terms.nextElementSibling !== more) {
          payBtn.insertBefore(terms, more);
        }
        return true;
      }
      if (terms.previousElementSibling !== payBtn) {
        payBtn.parentNode.insertBefore(terms, payBtn.nextSibling);
      }
      return false;
    }

    if (refine()) return;
    var self = this;
    var tries = 0;
    this._termsPoll = setInterval(function () {
      tries += 1;
      if (refine() || tries > 40) {
        clearInterval(self._termsPoll);
        self._termsPoll = null;
      }
    }, 250);
  };

  /**
   * Catches any buy attempt made before a design exists — bound on the buy
   * bar in capture phase, so it also covers express checkout iframes.
   */
  PlaymatDesigner.prototype.bindBuyGuard = function () {
    var buy = this.root.querySelector('.pmt-buy');
    if (!buy || buy.__pmtBuyGuard) return;
    buy.__pmtBuyGuard = true;
    var self = this;
    buy.addEventListener('click', function (e) {
      if (!self.requireArtwork || self.state.file) return;
      e.preventDefault();
      e.stopPropagation();
      self.say('Please upload your design first — both Add to cart and express checkout need it.', true);
      var zone = self.el.dropzone;
      if (zone) {
        zone.classList.remove('pmt-flash');
        void zone.offsetWidth;
        zone.classList.add('pmt-flash');
        setTimeout(function () { zone.classList.remove('pmt-flash'); }, 1400);
      }
      var target = self.el.canvasWrap || zone;
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);
  };

  PlaymatDesigner.prototype.alignPriceToButtons = function () {
    var price = this.root.querySelector('.pmt-price');
    var slot = this.el.buySlot;
    if (!price) return;
    var btn = (slot && slot.querySelector(
      '.addtocart-button, .shopify-payment-button__button, [name="add"]'));
    if (!btn || !slot) { price.style.marginTop = ''; return; }
    var offset = Math.round(btn.getBoundingClientRect().top -
                            slot.getBoundingClientRect().top);
    price.style.marginTop = (offset > 0 && offset < 200) ? offset + 'px' : '';
  };

  // ------------------------------------------------------------ cart handoff

  PlaymatDesigner.prototype.designNote = function () {
    var st = this.state;
    var parts = [];
    // Shopify replaces the uploaded filename with a hash; the customer's own
    // name for the file is recorded so a missing file can be asked for by name.
    if (st.file && st.file.name) parts.push(st.file.name);
    parts.push('Playmat ' + this.matWIn + 'x' + this.matHIn + 'in');
    if (st.zone) parts.push('zone: ' + st.zone);
    var zc = this.zoneColorProperty();
    if (zc) parts.push('zone colour ' + zc);
    if (st.finish) parts.push(st.finish);
    if (st.tube) parts.push(st.tube);
    parts.push('scale ' + st.scale.toFixed(2));
    parts.push('offset ' + st.tx.toFixed(2) + 'in,' + st.ty.toFixed(2) + 'in');
    parts.push('rotate ' + Math.round(st.rot * 180 / Math.PI) + 'deg');
    if (this.lastPPI) parts.push('src ' + Math.round(this.lastPPI) + 'ppi');
    return parts.join(' | ');
  };

  PlaymatDesigner.prototype.updateNote = function () {
    var form = this.form || this.findForm();
    if (!form) return;
    var note = form.querySelector('[data-pmt-prop="note"]');
    if (!note) return;
    note.value = this.state.file ? this.designNote() : '';
  };

  PlaymatDesigner.prototype.setFileOnInput = function (input, file) {
    if (!input) return;
    if (!file) { input.value = ''; return; }
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn('[playmat-designer] could not attach file to form input', e);
    }
  };

  /**
   * Injects the line-item-property inputs into the product form. Re-run after
   * every variant change: Metro replaces the buy-buttons innerHTML, which
   * destroys anything injected inside it — these live on the form itself, but
   * the re-run also re-attaches the file after any form re-render.
   */
  PlaymatDesigner.prototype.ensureFormInputs = function () {
    var form = this.findForm();
    if (!form) return;
    this.form = form;

    var self = this;
    function ensure(key, type, name) {
      var found = form.querySelector('[data-pmt-prop="' + key + '"]');
      if (!found) {
        found = document.createElement('input');
        found.type = type;
        found.name = name;
        found.setAttribute('data-pmt-prop', key);
        found.className = 'pmt-visually-hidden';
        if (type === 'file') found.setAttribute('tabindex', '-1');
        form.appendChild(found);
      }
      return found;
    }

    var art = ensure('art', 'file', 'properties[Artwork]');
    var shot = ensure('shot', 'file', 'properties[Design Preview]');
    var note = ensure('note', 'hidden', 'properties[_design]');
    var zcol = ensure('zonecolor', 'hidden', 'properties[Playzone Line Colour]');

    this.setFileOnInput(art, this.state.file);
    this.setFileOnInput(shot, this.shotFile);
    note.value = this.state.file ? this.designNote() : '';
    zcol.value = this.zoneColorProperty();
    zcol.disabled = !zcol.value;   // a disabled input never submits an empty property

    if (!form.__pmtGuard) {
      form.__pmtGuard = true;
      form.addEventListener('submit', function (e) {
        if (!self.requireArtwork) return;
        if (!self.state.file) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.say('Please upload your design before adding to cart.', true);
          self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }
  };

  PlaymatDesigner.prototype.updateGate = function () {
    if (!this.requireArtwork) return;
    var form = this.form || this.findForm();
    if (!form) return;
    var ready = !!this.state.file;
    var all = document.querySelectorAll('[name="add"]');
    var btns = [];
    for (var n = 0; n < all.length; n++) {
      if (all[n].form === form) btns.push(all[n]);
    }
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (ready) {
        if (b.getAttribute('data-pmt-blocked') === 'true') {
          b.removeAttribute('data-pmt-blocked');
          b.removeAttribute('aria-disabled');
          b.classList.remove('pmt-blocked');
        }
      } else {
        b.setAttribute('data-pmt-blocked', 'true');
        b.setAttribute('aria-disabled', 'true');
        b.classList.add('pmt-blocked');
      }
    }
    this.root.classList.toggle('pmt--ready', ready);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
